import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { ThemeProvider, ThemeContext } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Suspense, lazy } from 'react';
import { BrowserRouter } from 'react-router-dom';

// Performance: Lazy load non-critical components
// const Header = React.lazy(() => import('./components/Header')); // Load immediately as it's critical
const WhyPortal = React.lazy(() => import('./components/WhyPortal'));
const LiveSessionPreview = React.lazy(() => import('./components/LiveSessionPreview'));
const RecordedSessions = React.lazy(() => import('./components/RecordedSessions'));
const IndustryImages = React.lazy(() => import('./components/IndustryImages'));
const AdvisorySection = React.lazy(() => import('./components/landing/AdvisorySection'));
const BlogSection = React.lazy(() => import('./components/landing/BlogSection'));
const IntroGallery = React.lazy(() => import('./components/IntroGallery'));
const IntroGalleryTeaser = React.lazy(() => import('./components/IntroGalleryTeaser'));
const PPTSlider = React.lazy(() => import('./components/PPSlider'));
const Gallery = React.lazy(() => import('./pages/Gallery'));
const AllRecordedSessions = React.lazy(() => import('./components/AllRecordedSessions'));
const CallToAction = React.lazy(() => import('./components/CallToAction'));
const Footer = React.lazy(() => import('./components/Footer'));
// const AuthPopup = React.lazy(() => import('./components/AuthPopup'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const StudentProfile = React.lazy(() => import('./pages/StudentProfile'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = React.lazy(() => import('./pages/CookiePolicy'));
const Compliance = React.lazy(() => import('./pages/Compliance'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const FeedbackPage = React.lazy(() => import('./pages/FeedbackPage'));
const ProtectedRoute = React.lazy(() => import('./components/ProtectedRoute'));
const UnderConstruction = React.lazy(() => import('./pages/UnderConstruction'));
const ExpertDashboard = React.lazy(() => import('./pages/ExpertDashboard'));
const StudentDashboard = React.lazy(() => import('./pages/StudentDashboard'));
const ExpertListPage = React.lazy(() => import('./pages/ExpertListPage'));
const ExpertChat = React.lazy(() => import('./components/Chat/ExpertChat'));
const RoleBasedRoute = React.lazy(() => import('./components/RoleBasedRoute'));
const AdvisoryPage = React.lazy(() => import('./pages/AdvisoryPage'));
const BlogsPage = React.lazy(() => import('./pages/BlogsPage'));
const LatestUpdatesPage = React.lazy(() => import('./pages/LatestUpdatesPage'));
const LatestUpdateDetail = React.lazy(() => import('./pages/LatestUpdateDetail'));
const LearningResourcesPage = React.lazy(() => import('./pages/LearningResourcesPage'));
const ResourceListingPage = React.lazy(() => import('./pages/ResourceListingPage'));
const ResourceDetailPage = React.lazy(() => import('./pages/ResourceDetailPage'));

// Critical components loaded immediately
import HeroSection from './components/HeroSection';
import Header from './components/Header'; // Load immediately
import ToastManager from './components/Toast';
import ChatWidget from './components/Chatbot/ChatWidget';

// Define styled components outside with CSS custom properties for theme
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    height: 100%;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: var(--body-background, #6495ED);
    transition: background 0.3s ease;
  }

  /* Performance: Enhanced reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Performance: Mobile optimizations */
  @media (max-width: 768px) {
    * {
      -webkit-tap-highlight-color: transparent;
    }
    
    body {
      /* Disable hardware acceleration for unused effects */
      transform: translateZ(0);
      will-change: auto;
    }
    
    /* Reduce shadow complexity on mobile */
    * {
      box-shadow: none !important;
    }
    
    /* Simplify backdrop filters on mobile */
    * {
      backdrop-filter: none !important;
    }
  }

  /* Performance: Optimize scrolling */
  html {
    scroll-behavior: smooth;
    overflow-x: hidden;
  }

  /* Performance: Reduce paint complexity */
  img {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  height: 100%;
  background: var(--app-background, linear-gradient(135deg, #6495ED 0%, #4169E1 50%, #1E3A8A 100%));
  position: relative;
  overflow-x: hidden;
  transition: background 0.3s ease;
`;

const BackgroundAnimation = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(120, 119, 198, 0.3) 0%, transparent 70%);
    /* Performance: Very low frequency animation, pause when tab inactive */
    animation: ${props => props.$isTabVisible && !props.$isMobile && !props.$prefersReducedMotion ? 'float' : 'none'} 40s infinite ease-in-out;
  }
  
  &::before {
    width: 400px;
    height: 400px;
    top: 10%;
    left: 10%;
    animation-delay: 0s;
  }
  
  &::after {
    width: 600px;
    height: 600px;
    bottom: 10%;
    right: 10%;
    animation-delay: 20s;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    33% { transform: translateY(-15px) rotate(120deg); }
    66% { transform: translateY(15px) rotate(240deg); }
  }
  
  @media (max-width: 768px) {
    /* Disable background animation on mobile */
    &::before,
    &::after {
      animation: none !important;
    }
  }
`;

// App component with theme
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthPopupOpen, setIsAuthPopupOpen] = React.useState(false);
  const [authMode, setAuthMode] = React.useState('signup');
  const [isTabVisible, setIsTabVisible] = React.useState(true);
  const { isAuthenticated, user, loading: authLoading } = useAuth();

  // Check if current page is chat-related and should hide the AI bot
  const shouldHideChatBot = React.useMemo(() => {
    const chatRelatedPaths = ['/experts', '/expert/chat', '/expert/dashboard'];
    return chatRelatedPaths.some(path => location.pathname.startsWith(path));
  }, [location.pathname]);

  // const openAuthPopup = (mode) => {
  //   setAuthMode(mode);
  //   setIsAuthPopupOpen(true);
  // };

  // const closeAuthPopup = () => {
  //   setIsAuthPopupOpen(false);
  // };

  // Performance: Track tab visibility for background animations
  React.useEffect(() => {
    // Listen for custom auth popup events - DISABLED
    // const handleAuthPopupEvent = (event) => {
    //   const { mode } = event.detail;
    //   openAuthPopup(mode);
    // };

    // window.addEventListener('openAuthPopup', handleAuthPopupEvent);

    // return () => {
    //   window.removeEventListener('openAuthPopup', handleAuthPopupEvent);
    // };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Performance: Detect mobile and reduced motion
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <>
      <GlobalStyle />
      <AppContainer>
        <BackgroundAnimation 
          $isTabVisible={isTabVisible}
          $isMobile={isMobile}
          $prefersReducedMotion={prefersReducedMotion}
        />
        <Header 
          onLoginClick={() => navigate('/login')}
          onSignupClick={() => navigate('/signup')}
        />
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Routes>
              {/* Home page */}
              <Route path="/" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <HeroSection />
                  <IntroGalleryTeaser />
                  {/* <WhyPortal /> */}
                  {/* <LiveSessionPreview /> */}
                  {/* <RecordedSessions /> */}
                  {/* <IndustryImages /> */}
                  {/* <AdvisorySection /> */}
                  <BlogSection />
                  {/* <Gallery /> */}
                  {/* <PPTSlider /> */}
                  <CallToAction />
                  <Footer />
                </React.Suspense>
              } />

              {/* Authentication pages */}
              <Route path="/login" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <AuthPage mode="login" />
                </React.Suspense>
              } />
              <Route path="/signup" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <AuthPage mode="signup" />
                </React.Suspense>
              } />
              <Route path="/forgot-password" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <AuthPage mode="forgot-password" />
                </React.Suspense>
              } />

              {/* Protected routes */}
              <Route path="/student/dashboard" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <ProtectedRoute>
                    <RoleBasedRoute requiredRole="student">
                      <StudentDashboard />
                    </RoleBasedRoute>
                  </ProtectedRoute>
                </React.Suspense>
              } />
              <Route path="/profile" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <ProtectedRoute>
                    <RoleBasedRoute requiredRole="student">
                      <StudentProfile />
                    </RoleBasedRoute>
                  </ProtectedRoute>
                </React.Suspense>
              } />
              <Route path="/student/profile" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <ProtectedRoute>
                    <RoleBasedRoute requiredRole="student">
                      <StudentProfile />
                    </RoleBasedRoute>
                  </ProtectedRoute>
                </React.Suspense>
              } />
              <Route path="/expert/dashboard" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <RoleBasedRoute requiredRole="expert">
                    <ExpertDashboard />
                  </RoleBasedRoute>
                </React.Suspense>
              } />
              <Route path="/expert/chat" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <RoleBasedRoute requiredRole="expert">
                    <ExpertChat />
                  </RoleBasedRoute>
                </React.Suspense>
              } />
              <Route path="/advisory" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <ProtectedRoute>
                    <RoleBasedRoute requiredRole="student">
                      <AdvisoryPage />
                    </RoleBasedRoute>
                  </ProtectedRoute>
                </React.Suspense>
              } />
              <Route path="/experts" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <ProtectedRoute>
                    <RoleBasedRoute requiredRole="student">
                      <ExpertListPage />
                    </RoleBasedRoute>
                  </ProtectedRoute>
                </React.Suspense>
              } />

              {/* Other pages */}
              <Route path="/contact" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <ContactPage />
                </React.Suspense>
              } />
              <Route path="/blogs" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  {authLoading ? <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>Loading...</div> : isAuthenticated && user?.role === 'student' ? <BlogsPage /> : <Navigate to="/login" replace />}
                </React.Suspense>
              } />
              <Route path="/latest-updates" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  {authLoading ? <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>Loading...</div> : isAuthenticated && user?.role === 'student' ? <LatestUpdatesPage /> : <Navigate to="/login" replace />}
                </React.Suspense>
              } />
              <Route path="/latest-updates/:slug" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  {authLoading ? <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>Loading...</div> : isAuthenticated && user?.role === 'student' ? <LatestUpdateDetail /> : <Navigate to="/login" replace />}
                </React.Suspense>
              } />
              <Route path="/learning-resources" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  {authLoading ? <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>Loading...</div> : isAuthenticated && user?.role === 'student' ? <LearningResourcesPage /> : <Navigate to="/login" replace />}
                </React.Suspense>
              } />
              <Route path="/learning-resources/listing" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  {authLoading ? <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>Loading...</div> : isAuthenticated && user?.role === 'student' ? <ResourceListingPage /> : <Navigate to="/login" replace />}
                </React.Suspense>
              } />
              <Route path="/learning-resources/:slug" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  {authLoading ? <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>Loading...</div> : isAuthenticated && user?.role === 'student' ? <ResourceDetailPage /> : <Navigate to="/login" replace />}
                </React.Suspense>
              } />
              <Route path="/intro-gallery" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <IntroGallery />
                </React.Suspense>
              } />
              <Route path="/gallery" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <Gallery />
                </React.Suspense>
              } />
              <Route path="/privacy" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <PrivacyPolicy />
                </React.Suspense>
              } />
              <Route path="/terms" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <TermsOfService />
                </React.Suspense>
              } />
              <Route path="/cookies" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <CookiePolicy />
                </React.Suspense>
              } />
              <Route path="/compliance" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <Compliance />
                </React.Suspense>
              } />
              <Route path="/faq" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <FAQ />
                </React.Suspense>
              } />
              <Route path="/feedback" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <FeedbackPage />
                </React.Suspense>
              } />

              {/* Catch all route */}
              <Route path="*" element={
                <React.Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <UnderConstruction />
                </React.Suspense>
              } />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </AppContainer>
      
      {/* Global Toast Manager */}
      <ToastManager />
      
      {/* Chatbot Widget - Hidden on chat-related pages */}
      {!shouldHideChatBot && <ChatWidget />}
    </>
  );
}

// Main App component with providers
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
